(function () {
  'use strict';

  /* EDU Course list
   * Handles fetching and displaying a list of educational courses
   * courses imported from a csv uploaded to page metafields
   * 
   * TODO and notes
   * - check first col of first row of csv for valid date, if not, skip first row as it's probably headers
   * - after that we can just drop the column text directly in there as it's meant to be read anyways
   * - can also instead require a header row and use that to order the columns, so the marketing team can reorder it on their end
   * 
   * 
   * 
   * 
   */

  class EduCourseList {
    constructor () {
      // keep in-memory references to created DesktopCourseRow and MobileCourseCard elements
      this.container = null;
      this.headers = [];
      this.rows = [];
      this.courseData = []; // array of course objects with mapped fields + element instances
      this.desktopContent = null;
      this.mobileContent = null;
      this.selectedType = '';
      this.typeKey = null;
    }

    check_for_valid_resources () {
      const courseListUrl = document.querySelector('[data-course-list-url]')?.dataset?.courseListUrl;
      if (!courseListUrl || courseListUrl == '') {
        console.warn("[EDU COURSE LIST][WARNING] No course list URL provided");
        return false;
      }
      return true;
    }
    
    async init () {
      // reset stored instances on each init to avoid memory leaks on re-init
      this.instances = [];
      this.courseData = [];
      // get query parameters for preset filtering, if any
      const urlParams = new URLSearchParams(window.location.search);
      // normalize the preset type: ensure it's a trimmed, lowercase string so initial filtering is case-insensitive
      this.presetType = (urlParams.get('type') || '').trim().toLowerCase();
      // normalized form used for matching (strip non-alphanumeric so +, spaces, punctuation match)
      this.normalizedPresetType = this.presetType.replace(/[^a-z0-9]/g, '');
      
      this.container = document.querySelector('[data-course-list]');
      const courseListUrl = document.querySelector('[data-course-list-url]').dataset.courseListUrl;
      const courseList = await (await fetch(courseListUrl)).text();
      if (!courseList || courseList == '' || courseList.includes('404')) { throw new ReferenceError("Invalid course list data"); }


      // Parse CSV lines into rows; normalize each cell by trimming and replacing semicolons with commas
      this.rows = courseList.split('\n').map(row => row.split(',').map(cell => (cell === undefined || cell === null) ? '' : String(cell).trim().replace(/;/g, ',')));
      this.headers = (this.rows.shift() || []).map(h => String(h || '').trim()); // first row is headers

      // build normalized header keys (used for object properties)
      this.headerKeys = this.headers.map(h => String(h || '').toLowerCase().replace(/[^a-z0-9]+/g, '_'));
      // map column name for event type if present
      this.typeKey = this.headerKeys.find(k => k === 'event_type' || (k.includes('event') && k.includes('type')) ) || null;

    // date column handling is performed via headerKeys mapping

      // Build courseData objects with normalized keys and element instances
      this.courseData = this.rows.map(row => {
        const obj = { row };
        this.headerKeys.forEach((key, i) => {
          if (key === 'date') {
            const dateCell = row[i] || '';
            const [startDateRaw, endDateRaw] = String(dateCell || '').split(' - ').map(d => d && d.trim());
            obj.startDate = startDateRaw ? new Date(startDateRaw) : null;
            obj.endDate = endDateRaw ? new Date(endDateRaw) : null;
            obj.date = dateCell || '';
          } else {
            obj[key] = row[i] || '';
          }
        });

        // create desktop and mobile elements for each course and store references (originals kept in memory)
        const desktopEl = this.build_desktop_row(obj);
        const mobileEl = this.build_mobile_card(obj);
        obj.desktopElement = desktopEl;
        obj.mobileElement = mobileEl;
        return obj;
      });



      // Sort courseData by parsed startDate
      this.courseData.sort((a, b) => {
        if (a.startDate && b.startDate) return a.startDate - b.startDate;
        if (a.startDate) return -1;
        if (b.startDate) return 1;
        return 0;
      });

    this.container.style.gridTemplateColumns = `repeat(${this.headers.length}, minmax(0, 1fr))`;

      // build Set of event types for filtering by scanning rows (do NOT remove "Virtual")
      this.eventTypes = new Set();
      if (this.typeKey) {
        for (const course of this.courseData) {
          const cell = course[this.typeKey] || '';
          String(cell).split(/[,;]+/).map(t => t.trim()).filter(Boolean).forEach(t => this.eventTypes.add(t));
        }
      }
      // fallback: if scanning produced no types, use a sensible default list (ensure "Virtual" included)
      if (this.eventTypes.size === 0) {
        ['Excursions', 'R+COLOR', 'Tour', 'Studio Classes', 'Virtual'].forEach(t => this.eventTypes.add(t));
      }
    }

    build_desktop_row (row) {
      // Create a desktop-row custom element, let it render, then capture its rendered markup
      // and return a plain element containing the rendered cells. This avoids requiring
      // rowData on clones later (connectedCallback expects rowData) — clones will be static.
      const el = document.createElement('desktop-course-row');
      el.rowData = row.row || [];
      el.headers = this.headers;

      // Temporarily attach to document so connectedCallback runs and renders content.
      const temp = document.createElement('div');
      // keep it visually hidden/offscreen
      temp.style.position = 'absolute';
      temp.style.left = '-9999px';
      temp.style.top = '-9999px';
      temp.style.width = '0';
      temp.style.height = '0';
      temp.style.overflow = 'hidden';
      document.body.appendChild(temp);
      temp.appendChild(el);

      // Now el has rendered; extract the inner HTML into a plain container
      const wrapper = document.createElement('div');
      wrapper.classList.add('row');
      wrapper.innerHTML = el.innerHTML;

      // cleanup temporary render node
      document.body.removeChild(temp);

      // keep a reference (static node) for cloning later
      return wrapper;
    }

    construct_desktop_header () {
      const headerWrap = document.createElement('div');
      headerWrap.classList.add('desktop-course-list-header');

      // column header labels; when we hit the event-type column, append the select inside that header
      this.headers.forEach((header, idx) => {
        const el = document.createElement('div');
        el.classList.add('header');
        el.innerHTML = `<span>${header}</span>`;

        // if this header corresponds to the detected typeKey, add the select directly inside it
        if (this.typeKey && this.headerKeys[idx] === this.typeKey) {
          const filterEl = document.createElement('select');
          filterEl.classList.add('type-filter');
          const allOption = document.createElement('option');
          allOption.value = '';
          allOption.innerText = 'All Types';
          filterEl.appendChild(allOption);
          this.eventTypes.forEach(type => {
            const option = document.createElement('option');
            option.value = type;
            option.innerHTML = type;
            filterEl.appendChild(option);
          });

          // only set selectedType and regenerate; keep mobile select synchronized
          filterEl.addEventListener('change', (e) => {
            this.selectedType = e.target.value;
            if (this.mobileSelect) this.mobileSelect.value = e.target.value;
            this.regenerate_list();
          });

          this.desktopSelect = filterEl;
          // If a preset type was provided via URL, select the matching option now so
          // the subsequent call to regenerate_list() will apply the preset on desktop.
          if (this.presetType) {
            const normalize = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g,'');
            const match = Array.from(filterEl.options).find(opt => normalize(opt.value) === this.normalizedPresetType);
            if (match) {
              filterEl.value = match.value;
              this.selectedType = match.value;
              if (this.mobileSelect) this.mobileSelect.value = match.value;
            }
          }

          el.appendChild(filterEl);
        }

        headerWrap.appendChild(el);
      });

      // content container for desktop rows
      this.desktopContent = document.createElement('div');
      this.desktopContent.classList.add('desktop-course-list-content');

      // ensure header and desktop content use the same column layout as the main container
      const cols = `repeat(${this.headers.length}, minmax(0, 1fr))`;
      headerWrap.style.display = 'grid';
      headerWrap.style.gridTemplateColumns = cols;
      this.desktopContent.style.display = 'grid';
      this.desktopContent.style.gridTemplateColumns = cols;

      this.container.appendChild(headerWrap);
      this.container.appendChild(this.desktopContent);
    }

    regenerate_list () {
      // Clear desktop and mobile contents
      if (this.desktopContent) this.desktopContent.innerHTML = '';
      if (this.mobileContent) this.mobileContent.innerHTML = '';

      const normalize = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g,'');
      const target = normalize(this.selectedType || '');
      const useMobile = window.innerWidth <= 768;

      // iterate stored course objects and append clones of the appropriate element
      for (const course of this.courseData) {
        // determine course's types
        const raw = this.typeKey ? (course[this.typeKey] || '') : '';
        const myTypes = String(raw).split(/[,;]+/).map(t => normalize(t)).filter(Boolean);
        const matches = !target || myTypes.includes(target);
        if (!matches) continue;

        if (useMobile) {
          if (!this.mobileContent) continue;
          const clone = course.mobileElement.cloneNode(true);
          this.mobileContent.appendChild(clone);
        } else {
          if (!this.desktopContent) continue;
          const clone = course.desktopElement.cloneNode(true);
          this.desktopContent.appendChild(clone);
        }
      }
    }

    construct_desktop() {
      // Use regenerate_list to construct visible rows from stored courseData
      this.regenerate_list();
    }

    build_mobile_card (row) {
      // Accept a course object and create a mobile element reflecting its data
      const course = row; // 'row' is the course object
      const courseCard = document.createElement('mobile-course-card');
      courseCard.setAttribute('data-date', course.date || '');
      courseCard.setAttribute('data-start-date', course.startDate ? course.startDate.toISOString() : '');
      courseCard.setAttribute('data-end-date', course.endDate ? course.endDate.toISOString() : '');
      courseCard.setAttribute('data-title', course.title || '');
      courseCard.setAttribute('data-instructor', course.instructors || '');
      courseCard.setAttribute('data-location', course.location || '');
      courseCard.setAttribute('data-link', course.link || '');
      courseCard.setAttribute('data-type', this.typeKey ? (course[this.typeKey] || '') : '');
      return courseCard;
    }

    constructMobile () {
      const mobileContainer = document.createElement('div');
      mobileContainer.classList.add('mobile-course-list');
      // add event type filter select
      const filterEl = document.createElement('select');
      filterEl.classList.add('type-filter');
      const allOption = document.createElement('option');
      allOption.value = '';
      allOption.innerText = 'All Types';
      filterEl.appendChild(allOption);
      this.eventTypes.forEach(type => {
        const option = document.createElement('option');
        option.value = type;
        option.innerHTML = type;
        filterEl.appendChild(option);
      });

      // set selectedType only and regenerate_list; keep selects synchronized
      filterEl.addEventListener('change', (e) => {
        this.selectedType = e.target.value;
        if (this.desktopSelect) this.desktopSelect.value = e.target.value;
        this.regenerate_list();
      });
      mobileContainer.appendChild(filterEl);

      // mobile content container
      this.mobileContent = document.createElement('div');
      this.mobileContent.classList.add('mobile-course-list-content');
      mobileContainer.appendChild(this.mobileContent);

    // insert mobile container just after the main container
    this.container.parentNode.insertBefore(mobileContainer, this.container.nextSibling);

      // store reference to mobile select for synchronization
      this.mobileSelect = filterEl;

      // If a preset type is present, set the selected value and regenerate
      if (this.presetType) {
        const normalize = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g,'');
        const match = Array.from(filterEl.options).find(opt => normalize(opt.value) === this.normalizedPresetType);
        if (match) {
          filterEl.value = match.value;
          this.selectedType = match.value;
        }
        if (this.desktopSelect) this.desktopSelect.value = this.selectedType;
        this.regenerate_list();
      }
      // Always render initial mobile list
      this.regenerate_list();
    }
  }

  class DesktopCourseRow extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      const row = this.rowData || [];
      const headers = this.headers || [];
      this.classList.add('row');

      // determine index of the event type column up-front
      const typeIdx = (headers || []).findIndex(h => h.toLowerCase() === 'event type');
      row.forEach((cell, idx) => {
        const text = (cell === undefined || cell === null) ? '' : String(cell).trim();
        let el;

        // instagram handles (space-separated)
        if (text.toLowerCase().includes('@')) {
          el = document.createElement('div');
          el.classList.add('cell');

          const socials = text.split(' ').map(s => s.trim()).filter(Boolean);
          socials.forEach((social, i) => {
            const a = document.createElement('a');
            a.href = `https://www.instagram.com/${social.replace('@', '')}`;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.innerText = social;
            el.appendChild(a);
            if (i < socials.length - 1) el.appendChild(document.createTextNode(' '));
          });

        // semicolon-separated values -> show with commas
        } else if (text.includes(';') || idx === typeIdx) {
          // For semicolon-separated values or the event type column, normalize separators and preserve all tokens (including "Virtual")
          el = document.createElement('div');
          el.classList.add('cell');
          // Split on commas/semicolons and join for display (do not filter out "Virtual")
          const tokens = String(text).split(/[,;]+/).map(t => t.trim()).filter(Boolean);
          el.innerText = tokens.join(', ');
         
         // link cell (register)
         } else if (text.toLowerCase().includes('https')) {
           el = document.createElement('a');
           el.classList.add('cell');
           el.href = text;
           el.target = '_blank';
           el.rel = 'noopener noreferrer';
           el.innerText = 'Register';
        
        // plain text
        } else {
          el = document.createElement('div');
          el.classList.add('cell');
          el.innerText = text;
        }

        if (idx === 0) el.classList.add('first');
        if (idx === headers.length - 1) el.classList.add('last');

        this.appendChild(el);
      });

      // determine this row's type value so the element can filter itself later (keep original value)
      if (typeIdx !== -1) {
        this.type = (row[typeIdx] || '').toString().trim();
      } else {
        this.type = '';
      }
    }

    // New: hide if matches passed type (case-insensitive, supports comma/semicolon lists), otherwise show.
    filterByType(type) {
      if (!type) { this.style.display = ''; return; }
      const normalize = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g,'');
      const target = normalize(type);
      const myTypes = (this.type || '').split(/[,;]+/).map(t => normalize(t)).filter(Boolean);
      this.style.display = myTypes.includes(target) ? '' : 'none';
    }
  }

  // Replace MobileCourseCard.filterByType with normalized matching
  class MobileCourseCard extends HTMLElement {
    constructor() {
      super();
      this.markup = `
      <div class="card">
        <div class="course-title"></div>
        <div class="left-container">
          <div class="course-date"></div>
          <div class="course-type"></div>
          <div class="course-location"></div>
          <div class="course-instructor"></div>
        </div>
        <a class="course-link" target="_blank" rel="noopener noreferrer">Register</a>
      </div>
    `;
    }

    connectedCallback() {
      this.innerHTML = this.markup;
      this.querySelector('.course-date').innerText = this.getAttribute('data-date') || '';
      // Show types but strip any "Virtual" token from visible text while preserving data-type for filtering.
      const rawType = this.getAttribute('data-type') || '';
      const normalize = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g,'');
      const visibleType = String(rawType).split(/[,;]+/).map(t => t.trim()).filter(Boolean).filter(t => normalize(t) !== 'virtual').join(', ');
      this.querySelector('.course-type').innerText = visibleType;
      this.querySelector('.course-title').innerText = this.getAttribute('data-title') || '';
      this.querySelector('.course-instructor').innerText = this.getAttribute('data-instructor') || '';
      this.querySelector('.course-location').innerText = this.getAttribute('data-location') || '';
      const link = this.getAttribute('data-link') || '#';
      this.querySelector('.course-link').href = link;
      this.type = this.getAttribute('data-type') || '';
    }
    // New: hide if matches passed type (case-insensitive, supports comma/semicolon lists), otherwise show.
    filterByType(type) {
      if (!type) { this.style.display = ''; return; }
      const normalize = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g,'');
      const target = normalize(type);
      const myTypes = (this.type || '').split(/[,;]+/).map(t => normalize(t)).filter(Boolean);
      this.style.display = myTypes.includes(target) ? '' : 'none';
    }
  }

  customElements.define('mobile-course-card', MobileCourseCard);
  customElements.define('desktop-course-row', DesktopCourseRow);

  document.addEventListener("DOMContentLoaded", async () => {
    window.courseList = new EduCourseList();

    try {
      const canInit = window.courseList.check_for_valid_resources();
      if (!canInit) return;
      await window.courseList.init();
      if (window.innerWidth <= 768) {
        window.courseList.constructMobile();
      } else {
        window.courseList.construct_desktop_header();
        window.courseList.construct_desktop(); // Updated method call
      }
    } catch (error) {
      // Minimal error handling: log and show nothing heavy on the page
      console.error('EduCourseList init error:', error);
      // Optionally hide the container if present
      try { if (window.courseList && window.courseList.container) window.courseList.container.style.display = 'none'; } catch (e) {}
    }
  });

})();
